/**
 * Hash routes. Pure, so the route table is testable and one place decides what a URL means.
 *
 *   #/                 the front door: sign-in when signed out; the home tab or practice dashboard when in
 *   #/signin           sign-in on its own
 *   #/signin/link/<t>  an emailed one-time sign-in link
 *   #/invite/<t>       a clinic's invitation: create a patient account
 *   #/welcome          patient onboarding (four screens)
 *   #/home #/health #/coach #/profile   the patient tabs
 *   #/temp/log         the temperature flow: the one number that can mean go to hospital tonight
 *   #/symptoms         how you feel today, graded, with no temperature required
 *   #/log/weight #/log/intake #/log/bowel #/log/steps   the numbers a patient enters themselves
 *   #/inbox            the patient's conversation with their care team
 *   #/devices          connecting a cuff, a scale or a watch
 *   #/practice         the practice front page: a doctor's dashboard, a manager's patient list
 *   #/practice/patients          every enrolled patient (doctor)
 *   #/practice/patients/<id>     one patient
 *   #/practice/add               load and invite patients (manager)
 *   #/practice/messages[/<id>]   patient conversations (doctor)
 *   #/practice/roster  #/practice/enroll  #/practice/setup
 */

export type PatientTab = 'home' | 'health' | 'coach' | 'profile';
export type VitalKind = 'weight' | 'intake' | 'bowel' | 'steps';

export type Route =
  | { page: 'front' }
  | { page: 'signin' }
  | { page: 'signin-link'; token: string }
  | { page: 'invite'; token: string }
  | { page: 'welcome' }
  | { page: 'tab'; tab: PatientTab }
  | { page: 'temp-log' }
  | { page: 'symptoms' }
  | { page: 'vital-log'; kind: VitalKind }
  | { page: 'inbox' }
  | { page: 'devices' }
  | { page: 'practice' }
  | { page: 'practice-patients' }
  | { page: 'practice-patient'; patientId: string }
  | { page: 'practice-add' }
  | { page: 'practice-messages' }
  | { page: 'practice-thread'; patientId: string }
  | { page: 'practice-roster' }
  | { page: 'practice-enroll' }
  | { page: 'practice-setup' };

export const SIGNIN_HASH = '#/signin';
export const FRONT_HASH = '#/';

const TABS: readonly PatientTab[] = ['home', 'health', 'coach', 'profile'];

export function route(hash: string): Route {
  const path = (hash.replace(/^#/, '').split('?')[0] ?? '/').replace(/\/+$/, '') || '/';
  if (path === '/signin') return { page: 'signin' };
  const link = /^\/signin\/link\/([A-Za-z0-9_-]+)$/.exec(path);
  if (link?.[1]) return { page: 'signin-link', token: link[1] };
  const invite = /^\/invite\/([A-Za-z0-9_-]+)$/.exec(path);
  if (invite?.[1]) return { page: 'invite', token: invite[1] };
  if (path === '/welcome') return { page: 'welcome' };
  if (path === '/temp/log') return { page: 'temp-log' };
  if (path === '/inbox') return { page: 'inbox' };
  if (path === '/symptoms') return { page: 'symptoms' };
  if (path === '/devices') return { page: 'devices' };
  const vital = /^\/log\/(weight|intake|bowel|steps)$/.exec(path);
  if (vital?.[1]) return { page: 'vital-log', kind: vital[1] as VitalKind };
  const tab = /^\/([a-z]+)$/.exec(path)?.[1];
  if (tab && (TABS as readonly string[]).includes(tab)) return { page: 'tab', tab: tab as PatientTab };
  if (path === '/practice') return { page: 'practice' };
  if (path === '/practice/patients') return { page: 'practice-patients' };
  if (path === '/practice/add') return { page: 'practice-add' };
  if (path === '/practice/messages') return { page: 'practice-messages' };
  if (path === '/practice/roster') return { page: 'practice-roster' };
  if (path === '/practice/enroll') return { page: 'practice-enroll' };
  if (path === '/practice/setup') return { page: 'practice-setup' };
  const patient = /^\/practice\/patients\/([^/]+)$/.exec(path);
  if (patient?.[1]) return { page: 'practice-patient', patientId: decodeURIComponent(patient[1]) };
  const thread = /^\/practice\/messages\/([^/]+)$/.exec(path);
  if (thread?.[1]) return { page: 'practice-thread', patientId: decodeURIComponent(thread[1]) };
  return { page: 'front' };
}

export function goTo(hash: string): void {
  if (location.hash === hash) return;
  location.hash = hash;
}

export function tabHash(tab: PatientTab): string {
  return `#/${tab}`;
}

export function patientHash(patientId: string): string {
  return `#/practice/patients/${encodeURIComponent(patientId)}`;
}

export function threadHash(patientId: string): string {
  return `#/practice/messages/${encodeURIComponent(patientId)}`;
}

const RETURN_KEY = 'oncocoach.returnTo';

export function rememberReturn(hash: string = location.hash): void {
  try {
    sessionStorage.setItem(RETURN_KEY, hash || FRONT_HASH);
  } catch {
    /* they land on the front door */
  }
}

export function takeReturn(): string | null {
  try {
    const hash = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return hash && hash !== location.hash ? hash : null;
  } catch {
    return null;
  }
}

const WELCOMED_KEY = 'oncocoach.welcomed';

/** Whether this browser has walked the onboarding screens for this person. */
export function hasWelcomed(personId: string): boolean {
  try {
    return localStorage.getItem(WELCOMED_KEY) === personId;
  } catch {
    return true;
  }
}

export function markWelcomed(personId: string): void {
  try {
    localStorage.setItem(WELCOMED_KEY, personId);
  } catch {
    /* fine */
  }
}
